import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
  arrayUnion,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "../firebase";


export interface GuestSummary {
  avgRating: number;
  cleanliness: number;
  communication: number;
  cooperation: number;
  followedRules: number;
  behaviour: number;
  totalReviews: number;
  lastUpdated: string;
}


export interface HostGuestRating {
  guestId: string;
  reservationId?: string;
  hostId: string;
  dateCreated: string;
  cleanliness: number;
  communication: number;
  cooperation: number;
  followedRules: number;
  behaviour: number;
  overallRating: "Excellent" | "Superb" | "Very Nice" | "Worth It" | "Not Worth It";
  hostReview: string;
}

export interface GuestRatingState {
  summaries: Record<string, GuestSummary | null>;
    ratings: Record<string, HostGuestRating[]>;
  loading: boolean;
  error: string | null;
  reviewed: boolean;
  currentReview: HostGuestRating | null;
}

const initialState: GuestRatingState = {
  ratings: {},
  loading: false,
  error: null,
  reviewed: false,
  currentReview: null,
  summaries: {},

};

export const submitGuestRating = createAsyncThunk(
  "guestRatings/submit",
  async ({
    guestId,
    reservationId,
    hostId,
    hostReview,
    overallRating,
    criteria,
  }: {
    guestId: string;
    reservationId: string;
    hostId: string;
    hostReview: string;
    overallRating: string;
    criteria: Record<string, number>;
  }) => {
    const ref = doc(db, "shortguestratings", guestId);
    const snapshot = await getDoc(ref);

    const newRating: HostGuestRating = {
      guestId,
      reservationId,
      hostId,
      hostReview,
      overallRating: overallRating as HostGuestRating["overallRating"],
      cleanliness: criteria["Cleanliness"] || 0,
      communication: criteria["Communication"] || 0,
      cooperation: criteria["Cooperation"] || 0,
      followedRules: criteria["Followed Rules"] || 0,
      behaviour: criteria["Behaviour"] || 0,
      dateCreated: Timestamp.now().toDate().toISOString(),
    };

    if (snapshot.exists()) {
      const existing = snapshot.data();
      const alreadyReviewed = existing.ratings?.some(
        (r: any) => r.reservationId === reservationId
      );
      if (alreadyReviewed) return { reviewed: true, guestId };

      await updateDoc(ref, {
        ratings: arrayUnion(newRating),
      });
    } else {
      await setDoc(ref, {
        guestId,
        ratings: [newRating],
      });
    }

    return { reviewed: false, guestId };
  }
);

export const fetchGuestRatingsByGuest = createAsyncThunk<
  { guestId: string; ratings: HostGuestRating[] },
  string,
  { rejectValue: string }
>("guestRatings/fetchByGuest", async (guestId, { rejectWithValue }) => {
  try {
    const ref = doc(db, "shortguestratings", guestId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { guestId, ratings: [] };
    return {
      guestId,
      ratings: snap.data().ratings || [],
    };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const fetchGuestRatingByReservation = createAsyncThunk<
  HostGuestRating | null,
  { guestId: string; reservationId: string },
  { rejectValue: string }
>("guestRatings/fetchByReservation", async ({ guestId, reservationId }, { rejectWithValue }) => {
  try {
    const ref = doc(db, "shortguestratings", guestId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const ratings: HostGuestRating[] = snap.data().ratings || [];
    return ratings.find((r) => r.reservationId === reservationId) || null;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});


export const fetchAllGuestRatings = createAsyncThunk(
  "guestRatings/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getDocs(collection(db, "shortguestratings"));
      const allRatings: Record<string, HostGuestRating[]> = {};
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        allRatings[data.guestId] = data.ratings || [];
      });
      return allRatings;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);


const shortGuestRatingSlice = createSlice({
  name: "guestRatings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(submitGuestRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitGuestRating.fulfilled, (state, action) => {
        state.reviewed = action.payload.reviewed;
        state.loading = false;
      })
      .addCase(submitGuestRating.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchGuestRatingsByGuest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGuestRatingsByGuest.fulfilled, (state, action) => {
        const { guestId, ratings } = action.payload;
        state.ratings[guestId] = ratings;
        state.loading = false;
      })
      .addCase(fetchGuestRatingsByGuest.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchAllGuestRatings.fulfilled, (state, action) => {
        state.ratings = action.payload;
        state.loading = false;
      })
      
      .addCase(fetchGuestRatingByReservation.fulfilled, (state, action: PayloadAction<HostGuestRating | null>) => {
        state.currentReview = action.payload;
      });
  },
});

export default shortGuestRatingSlice.reducer;
